import { useEffect, useState } from 'react';
import { useAppContext } from '@/App';
import { getMe } from '@/lib/api';
import { fullRegionName } from '@/lib/addressRegions';
import { formatDateValue, parseDateValue } from '@/lib/dates';
import AccountSettingsCard from '@/components/profile/AccountSettingsCard';
import ProfileHero from '@/components/profile/ProfileHero';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import {
  User, Mail, Phone, MapPin, Calendar, Shield, GraduationCap,
  BookOpen, Languages, Building2,
} from 'lucide-react';

const NAVY = '#062B1A';
const GOLD = '#D4AF37';

function genderLabel(g) {
  const map = { male: 'Male', female: 'Female', other: 'Other', prefer_not_to_say: 'Prefer not to say' };
  return map[g] || g || 'Not set';
}

function graduationPercent(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const start = parseDateValue(startDate)?.getTime() ?? Number.NaN;
  const end = parseDateValue(endDate)?.getTime() ?? Number.NaN;
  const now = Date.now();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;
  const pct = ((now - start) / (end - start)) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

function formatDate(value, fallback) {
  return formatDateValue(value, { fallback });
}

function formatCurrency(v) {
  return '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function agencyDisplayName(student) {
  const agency = student?.agency;
  if (agency && typeof agency === 'object') {
    return agency.name || agency.company_name || agency.agent_name || agency.email || '';
  }
  return student?.agency_name || student?.agent_name || '';
}

function TuitionChart({ paid, total }) {
  const remaining = Math.max(0, (total || 0) - (paid || 0));
  const data = [
    { name: 'Paid', value: paid || 0 },
    { name: 'Remaining', value: remaining },
  ];
  const colors = [GOLD, '#E5E7EB'];
  if (!total) return <p className="text-sm text-[#062B1A]/50">No tuition information</p>;
  return (
    <div className="flex flex-col items-center">
      <ResponsiveContainer width={180} height={180}>
        <PieChart>
          <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={2}>
            {data.map((_, i) => <Cell key={i} fill={colors[i]} />)}
          </Pie>
          <Tooltip formatter={(v) => formatCurrency(v)} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex gap-4 mt-2 text-xs">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: GOLD }} /> Paid {formatCurrency(paid)}</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-200" /> Remaining {formatCurrency(remaining)}</span>
      </div>
    </div>
  );
}

function LoanChart({ paid, total }) {
  const remaining = Math.max(0, (total || 0) - (paid || 0));
  const data = [
    { name: 'Repaid', value: paid || 0 },
    { name: 'Outstanding', value: remaining },
  ];
  const colors = ['#22C55E', '#E5E7EB'];
  if (!total) return <p className="text-sm text-[#062B1A]/50">No loan information</p>;
  return (
    <div className="flex flex-col items-center">
      <ResponsiveContainer width={180} height={180}>
        <PieChart>
          <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={2}>
            {data.map((_, i) => <Cell key={i} fill={colors[i]} />)}
          </Pie>
          <Tooltip formatter={(v) => formatCurrency(v)} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex gap-4 mt-2 text-xs">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> Repaid {formatCurrency(paid)}</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-200" /> Outstanding {formatCurrency(remaining)}</span>
      </div>
    </div>
  );
}

export default function StudentProfile() {
  const { currentStudent } = useAppContext();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await getMe();
        setStudent(res.data?.student || currentStudent || null);
      } catch {
        setStudent(currentStudent);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentStudent]);

  if (loading) {
    return (
      <div className="space-y-4 animate-fade-in">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-[#F7F8FA] animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (!student) {
    return (
      <div className="space-y-6 animate-fade-in">
        <AccountSettingsCard />
        <p className="text-[#062B1A]/60">Student profile not found.</p>
      </div>
    );
  }

  const gradPct = graduationPercent(student.start_date, student.end_date);
  const linkedAgencyName = agencyDisplayName(student);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Profile header */}
      <ProfileHero
        avatar={(
          <Avatar className="h-24 w-24 border-4 border-white shadow-md">
            {student.photo && <AvatarImage src={student.photo} alt={student.name} />}
            <AvatarFallback className="bg-[#D4AF37] text-2xl font-bold text-[#062B1A]">
              {student.name?.split(' ').map((p) => p[0]).join('') || '?'}
            </AvatarFallback>
          </Avatar>
        )}
      >
        <h2 className="break-words text-2xl font-semibold text-white" style={{ fontFamily: 'var(--font-display)' }}>
          {student.name}
        </h2>
        <div className="flex items-center gap-2 break-all text-sm text-white/90">
          <Mail className="h-4 w-4 shrink-0" /> {student.email}
        </div>
        <Badge variant="outline" className="border-white/40 bg-white font-semibold text-[#062B1A]">
          Student
        </Badge>
      </ProfileHero>

      <AccountSettingsCard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Info */}
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#062B1A] flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
              <User className="w-4 h-4" /> Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={Calendar} label="Date of Birth" value={formatDate(student.dob, 'Not set')} />
            <InfoRow icon={User} label="Gender" value={genderLabel(student.gender)} />
            <InfoRow icon={Phone} label="Phone" value={student.phone || 'Not set'} />
            {(student.unit_number || student.street_address || student.city || student.country || student.province || student.postal_code) ? (
              <>
                <InfoRow icon={MapPin} label="Apt/Unit/Suite #" value={student.unit_number || 'Not set'} />
                <InfoRow icon={MapPin} label="Street Address" value={student.street_address || 'Not set'} />
                <InfoRow icon={MapPin} label="City" value={student.city || 'Not set'} />
                <InfoRow icon={MapPin} label="Country" value={student.country || 'Not set'} />
                <InfoRow icon={MapPin} label="Province/State" value={fullRegionName(student.country, student.province) || 'Not set'} />
                <InfoRow icon={MapPin} label="Postal Code" value={student.postal_code || 'Not set'} />
              </>
            ) : (
              <InfoRow icon={MapPin} label="Address" value={student.address || 'Not set'} />
            )}
            <InfoRow icon={Shield} label="SIN" value={student.sinMask || '---'} />
            <InfoRow icon={BookOpen} label="Program" value={student.program_name || 'Not set'} />
            {(linkedAgencyName || student.agent_id) && (
              <InfoRow icon={Building2} label="Agency" value={linkedAgencyName || 'Linked agency'} />
            )}
            {student.english_proficiency_type && (
              <InfoRow icon={Languages} label="English Proficiency" value={
                `${student.english_proficiency_type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}${student.english_proficiency_score ? ` (${student.english_proficiency_score})` : ''}`
              } />
            )}
          </CardContent>
        </Card>

        {/* Graduation Progress */}
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#062B1A] flex items-center gap-2" style={{ fontFamily: 'var(--font-display)' }}>
              <GraduationCap className="w-4 h-4" /> Graduation Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {gradPct !== null ? (
              <>
                <div className="flex justify-between text-sm text-[#062B1A]/70 mb-1">
                  <span>{formatDate(student.start_date, 'Start')}</span>
                  <span>{formatDate(student.end_date, 'End')}</span>
                </div>
                <Progress value={gradPct} className="h-4 [&>div]:bg-[#D4AF37]" />
                <p className="text-center text-sm font-medium text-[#062B1A]">{gradPct}% through program</p>
              </>
            ) : (
              <p className="text-sm text-[#062B1A]/50">Start and end dates are not set.</p>
            )}
            {student.grade_snapshot?.average_percentage != null && (
              <div className="pt-2 border-t border-[rgba(6, 43, 26,0.08)]">
                <p className="text-sm text-[#062B1A]/70">
                  Current average: <span className="font-semibold text-[#062B1A]">{student.grade_snapshot.average_percentage}%</span>
                  <span className="ml-2 text-xs text-[#062B1A]/50">({student.grade_snapshot.graded_items} items graded)</span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Financial Section */}
      <div className={`grid grid-cols-1 ${student.has_student_loan ? 'lg:grid-cols-2' : ''} gap-6`}>
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              Tuition
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TuitionChart paid={student.tuition_paid} total={student.tuition_total} />
          </CardContent>
        </Card>

        {student.has_student_loan && (
          <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
                Student Loan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <LoanChart paid={student.loan_paid} total={student.loan_total} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <Icon className="w-4 h-4 text-[#062B1A]/40 flex-shrink-0" />
      <span className="text-[#062B1A]/60 w-28 flex-shrink-0">{label}</span>
      <span className="text-[#062B1A] font-medium min-w-0 break-words">{value}</span>
    </div>
  );
}
